import { Card, CardContent } from "@/components/ui/card";
import { BookOpen, Sparkles, Moon, Star, Heart, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { MOOD_OPTIONS } from "@/lib/journal-constants";

const JournalEntriesList = ({ entries, selectedEntry, onSelect }) => {
  if (entries.length === 0) {
    return (
      <Card className="glass">
        <CardContent className="py-12 text-center">
          <BookOpen className="w-12 h-12 text-mystic-500 mx-auto mb-4" />
          <h3 className="text-xl text-white mb-2">No Journal Entries Yet</h3>
          <p className="text-mystic-200/70">
            Start your journaling journey today to see your history here.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="journal-entries-list">
      {entries.map((entry, idx) => {
        const isOpen = selectedEntry?.date === entry.date;
        return (
          <motion.div
            key={entry.entry_id || idx}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05 }}
          >
            <Card
              className="glass cursor-pointer hover:border-mystic-300/30 transition-all"
              onClick={() => onSelect(isOpen ? null : entry)}
              data-testid={`journal-entry-${entry.date}`}
            >
              <CardContent className="py-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-white font-medium">
                        {new Date(entry.date).toLocaleDateString("en-US", {
                          weekday: "long",
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                      {entry.mood && (
                        <span className="text-sm px-2 py-1 glass-light rounded-full text-mystic-200">
                          {MOOD_OPTIONS.find((m) => m.value === entry.mood)?.emoji} {entry.mood}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-mystic-300">
                      {entry.cycle_phase && (
                        <span className="flex items-center gap-1">
                          <Sparkles className="w-3 h-3" />
                          {entry.cycle_phase}
                        </span>
                      )}
                      {entry.moon_sign && (
                        <span className="flex items-center gap-1">
                          <Moon className="w-3 h-3" />
                          Moon in {entry.moon_sign}
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight
                    className={`w-5 h-5 text-mystic-400 transition-transform ${isOpen ? "rotate-90" : ""}`}
                  />
                </div>

                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-4 pt-4 border-t border-white/10 space-y-4"
                    >
                      {entry.content && (
                        <div>
                          <h4 className="text-mystic-300 text-sm font-medium mb-2">Journal Entry</h4>
                          <p className="text-white/90 whitespace-pre-wrap">{entry.content}</p>
                        </div>
                      )}
                      {entry.gratitude && (
                        <div>
                          <h4 className="text-mystic-300 text-sm font-medium mb-2 flex items-center gap-2">
                            <Heart className="w-4 h-4 text-pink-400" />
                            Gratitude
                          </h4>
                          <p className="text-white/90">{entry.gratitude}</p>
                        </div>
                      )}
                      {entry.intentions && (
                        <div>
                          <h4 className="text-mystic-300 text-sm font-medium mb-2 flex items-center gap-2">
                            <Star className="w-4 h-4 text-moon-gold" />
                            Intentions
                          </h4>
                          <p className="text-white/90">{entry.intentions}</p>
                        </div>
                      )}
                      {entry.guided_questions?.length > 0 && (
                        <div>
                          <h4 className="text-mystic-300 text-sm font-medium mb-2">Guided Reflections</h4>
                          <div className="space-y-3">
                            {entry.guided_questions.map((q, qIdx) => (
                              <div key={q.question || `q-${qIdx}`} className="glass-light rounded-lg p-3">
                                <p className="text-mystic-200 text-sm italic mb-1">{q.question}</p>
                                <p className="text-white/90">{q.response}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </CardContent>
            </Card>
          </motion.div>
        );
      })}
    </div>
  );
};

export default JournalEntriesList;
